import React, { useCallback, useMemo, useRef } from 'react'
import { Button, H6, InfoSheetPanel, Row, Separator, Divider, useOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import ZionForm, { useZionForm } from '@fs/zion-form'
import { NoticeLoading, ControlSplit } from '@fs/zion-icon'
import cloneDeep from 'lodash.clonedeep'
import { getNestedOrFloatingElementById, useSlsHighlightsContext, useSlsStateContext } from '@fs/zion-sls'
import { useFeatureFlag } from '@fs/zion-flags'
import { useSharedBrowserStateContext } from '../../../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { useOverlayContext } from '../../../../../../../shared/providers/overlayContextProvider/OverlayContext'
import { useR2DispatchContext, useR2StateContext } from '../../../../../../providers/r2Provider/R2Context'
import PanelHeader from '../../../shared/PanelHeader'
import {
  createDispatchesForNewField,
  getFieldText,
  getFormDefaultValues,
  getPersonsByArk,
  getPersonsFromField,
  handleSubmitLinkFieldsViaHighlights,
  TEMPORARY_FIELD_ID,
} from '../../utils/SharedHighlightUtils'
import { getFieldsFromSharedHighlights } from '../../../highlightPanel/utils/highlightUtils'
import { insertHighlight } from '../../../../../../utils/imageHighlightUtils'
import SharedHighlightPersonCheckbox from './SharedHighlightPersonCheckbox'
import useUnsavedChangesOverlay from '../../../../../../hooks/useUnsavedChangesOverlay'
import UnsavedChangesOverlay from '../../../shared/UnsavedChangesOverlay'

/**
 * SelectPersons Component
 *
 * This component allows users to select which persons (names) should be linked to a field
 * when adding highlights. It handles three distinct flows:
 *
 * FLOW 1: New Person + New/Existing Field
 * - User is creating a brand new person (name) in the system
 * - Highlights and field info are prepared but NOT saved yet
 * - Actual save happens when the person is created (see AddPerson.js)
 * - For fullTextAttachFlowV2, dispatches are also prepared immediately
 *
 * FLOW 2: Existing Person + New Field
 * - User is adding a new field to an existing person
 * - Creates field structure and links it to selected persons via highlights
 * - Saves immediately with all necessary dispatches
 *
 * FLOW 3: Existing Person + Existing Field
 * - User is modifying which persons share an existing field
 * - Clones the field and updates person associations
 * - Handles complex sharing scenarios (NAME_SURNAME level changes, etc.)
 *
 * The form displays all persons that currently share any field on the selected highlights,
 * grouped by image (ARK). Users check/uncheck persons to control field sharing.
 */
const SelectPersons = ({ fieldValue, handleSave, saving, onDiscard }) => {
  const { editSharedHighlightPage2Overlay, closeOverlay } = useOverlayContext()
  const [t] = useTranslation()
  const h = useSlsHighlightsContext()
  const r2State = useR2StateContext()
  const r2Dispatch = useR2DispatchContext()
  const zionSlsState = useSlsStateContext()
  const thumbnails = useSharedBrowserStateContext().thumbnails
  const isNewPerson = !getNestedOrFloatingElementById(zionSlsState.images, r2State.selectedPersonId)
  const activeField = getNestedOrFloatingElementById(zionSlsState.images, r2State.highlightsFieldInfo?.id)
  const isNewField = !activeField
  const handleClose = useCallback(() => closeOverlay('editSharedHighlightPage2'), [closeOverlay])
  const unsavedChangesOverlay = useOverlay()
  const saveButtonRef = useRef(null)
  const fullTextAttachFlowV2Flag = useFeatureFlag('rip_sharedImageViewer_fullTextAttachFlow_v2')

  const attachedHighlights = useMemo(
    // eslint-disable-next-line no-unsafe-optional-chaining
    () => [...r2State?.selectedHighlights.main, ...r2State?.selectedHighlights.splitScreen],
    [r2State?.selectedHighlights]
  )

  const currentFieldsOnHighlights = useMemo(() => {
    const fieldsAttachedToHighlights = getFieldsFromSharedHighlights({
      highlights: attachedHighlights,
      images: zionSlsState?.images,
      types: zionSlsState?.types,
    })

    return fieldsAttachedToHighlights || []
  }, [zionSlsState?.images, zionSlsState?.types, attachedHighlights])

  const personsFromField = useMemo(
    () =>
      getPersonsFromField({
        activeField,
        isNewPerson,
        isNewField,
        currentFieldsOnHighlights,
        images: zionSlsState.images,
        selectedPersonId: r2State.selectedPersonId,
      }),
    [activeField, isNewField, isNewPerson, r2State.selectedPersonId, currentFieldsOnHighlights, zionSlsState.images]
  )

  const allTheArks = useMemo(
    () =>
      getPersonsByArk({
        allPersonsSharingField: personsFromField,
        selectedPersonId: r2State.selectedPersonId || r2State.createdPersonId || h.activeFieldInfo?.personId,
        currentImageArk: zionSlsState.currentImageArk,
        isNewPerson,
      }),
    [
      personsFromField,
      isNewPerson,
      r2State.selectedPersonId,
      r2State.createdPersonId,
      h.activeFieldInfo?.personId,
      zionSlsState.currentImageArk,
    ]
  )

  const personsSharingFieldOnImage = useMemo(
    () => allTheArks[zionSlsState.currentImageArk] || [],
    [allTheArks, zionSlsState.currentImageArk]
  )

  const allPersonIdsSharingField = useMemo(() => personsFromField.map((person) => person.id), [personsFromField])

  const moreThanThirtyPersonsOnHighlights = useMemo(
    () => allPersonIdsSharingField?.length > 30,
    [allPersonIdsSharingField]
  )

  const allTheArksExceptCurrent = useMemo(() => {
    if (allTheArks) {
      const clone = cloneDeep(allTheArks)
      delete clone[zionSlsState.currentImageArk]
      return clone
    }
    return {}
  }, [allTheArks, zionSlsState.currentImageArk])

  const fieldTypeLabel = useMemo(
    () => r2State.highlightsFieldInfo?.displayLabel || r2State.highlightsFieldInfo?.label,
    [r2State.highlightsFieldInfo]
  )

  const headerText = useMemo(
    () =>
      moreThanThirtyPersonsOnHighlights
        ? t(
            'add-highlight-linked-fields.choose-others-on-image.more-than-thirty-heading',
            'All names selected will continue to link to the {fieldType} because the field is linked to too many names.',
            { fieldType: fieldTypeLabel }
          )
        : t(
            'add-highlight-linked-fields.choose-others-on-image.heading',
            'Select every name that should continue to link to the {fieldType}.',
            { fieldType: fieldTypeLabel }
          ),
    [moreThanThirtyPersonsOnHighlights, fieldTypeLabel, t]
  )

  const formDefaultValues = useMemo(
    () => getFormDefaultValues({ personIds: allPersonIdsSharingField, selectedPersonId: r2State.selectedPersonId }),
    [allPersonIdsSharingField, r2State.selectedPersonId]
  )

  const splitScreenImageArk = thumbnails?.[r2State.referenceFocusedIndex]?.ark

  const formMethods = useZionForm({
    mode: 'all',
    defaultValues: formDefaultValues,
  })

  const linkedFieldText = useMemo(() => fieldValue?.displayValue || getFieldText(fieldValue), [fieldValue])

  /**
   * Helper function to extract person IDs from form values
   * @param {Object} values - ZionForm key-values representing selected persons
   * @returns {Array} Array of selected person IDs
   */
  const getSelectedPersonIdsFromFormValues = useCallback((values) => {
    const personIds = []
    Object.entries(values)
      .filter(([key]) => key.startsWith('1:1:'))
      .forEach(([key, value]) => {
        if (value) personIds.push(key)
      })
    return personIds
  }, [])

  /**
   * Gets highlight dispatches for the current state
   * These dispatches represent the ADD_RECT operations from the highlight hook
   */
  const getHighlightDispatches = useCallback(
    () =>
      h.getDispatches({
        newFieldId: isNewPerson ? undefined : TEMPORARY_FIELD_ID,
        mainImageArk: zionSlsState.currentImageArk,
        disassociateRectangle: true,
        splitScreenImageArk: splitScreenImageArk || undefined,
      }),
    [h, zionSlsState.currentImageArk, splitScreenImageArk, isNewPerson]
  )

  /**
   * Prepares dispatches for a new person being created.
   * This also stores the highlights and field info in R2 state
   * to be processed when the person is saved (for backwards compatibility with the existing flow).
   *
   * For the new flow (v2), it also prepares the full dispatches immediately.
   *
   * @param {Object} values - Form values with selected person IDs
   */
  const prepareDispatchesForNewPerson = useCallback(
    (values) => {
      const selectedPersonIds = getSelectedPersonIdsFromFormValues(values)

      // Update highlights with selected person IDs
      let updatedEditHighlights = [...r2State.editHighlights]
      attachedHighlights.forEach((highlight) => {
        highlight.personIds = selectedPersonIds
        highlight.selected = false
        updatedEditHighlights = insertHighlight(updatedEditHighlights, highlight)
      })

      r2Dispatch({ type: 'SET_EDIT_HIGHLIGHTS', value: updatedEditHighlights })

      // Store field info for later use when person is created
      const newHighlightsFieldInfo = { ...r2State.highlightsFieldInfo, value: fieldValue?.value }
      r2Dispatch({ type: 'SET_HIGHLIGHTS_FIELD_INFO', value: newHighlightsFieldInfo })

      // For the new cards flow (v2), also prepare and save the full dispatches
      if (fullTextAttachFlowV2Flag.isOn) {
        const highlightDispatches = getHighlightDispatches()
        const dispatchData = createDispatchesForNewField({
          personIdValues: values,
          selectedField: fieldValue,
          activeField: h.activeFieldInfo,
          partitionKey: zionSlsState.currentImageArk,
          originalHighlightDispatches: [...highlightDispatches],
          images: zionSlsState.images,
          sharedElements: currentFieldsOnHighlights,
          selectedPersonId: r2State.createdPersonId || r2State.selectedPersonId || h.activeFieldInfo?.personId,
          involvedPersons: personsFromField,
          types: zionSlsState.types,
          isNewPerson,
        })

        // Pass the dispatches and related data to the parent component
        handleSave(dispatchData)
      }

      // Close all highlight-related overlays
      closeOverlay('highlightsPanel')
      closeOverlay('highlightsPanelOld')
      closeOverlay('editSharedHighlightPage2')
      closeOverlay('editSharedHighlightPage1')
    },
    [
      attachedHighlights,
      closeOverlay,
      currentFieldsOnHighlights,
      fieldValue,
      fullTextAttachFlowV2Flag,
      getHighlightDispatches,
      getSelectedPersonIdsFromFormValues,
      h.activeFieldInfo,
      handleSave,
      personsFromField,
      r2Dispatch,
      r2State.createdPersonId,
      r2State.editHighlights,
      r2State.highlightsFieldInfo,
      r2State.selectedPersonId,
      zionSlsState.currentImageArk,
      zionSlsState.images,
      zionSlsState.types,
      isNewPerson,
    ]
  )

  /**
   * Handles the case of linking a new field to existing persons via highlights.
   * Creates all necessary dispatches for field creation and person associations
   */
  const prepareDispatchesForNewField = useCallback(
    (values) => {
      const highlightDispatches = getHighlightDispatches()
      const dispatchData = createDispatchesForNewField({
        personIdValues: values,
        selectedField: fieldValue,
        activeField: h.activeFieldInfo,
        partitionKey: zionSlsState.currentImageArk,
        originalHighlightDispatches: [...highlightDispatches],
        images: zionSlsState.images,
        sharedElements: currentFieldsOnHighlights,
        selectedPersonId: r2State.createdPersonId || r2State.selectedPersonId || h.activeFieldInfo?.personId,
        involvedPersons: personsFromField,
        types: zionSlsState.types,
        isNewPerson,
      })
      handleSave(dispatchData)
    },
    [
      currentFieldsOnHighlights,
      fieldValue,
      getHighlightDispatches,
      h.activeFieldInfo,
      handleSave,
      personsFromField,
      r2State.createdPersonId,
      r2State.selectedPersonId,
      zionSlsState.currentImageArk,
      zionSlsState.images,
      zionSlsState.types,
      isNewPerson,
    ]
  )

  /**
   * Handles linking an existing field to different persons via highlights
   * Modifies field associations and moves shared elements between persons
   */
  const prepareDispatchesForExistingFields = useCallback(
    (values) => {
      const highlightDispatches = getHighlightDispatches()
      const dispatchData = handleSubmitLinkFieldsViaHighlights({
        values,
        isNewField: false,
        linkedFieldValue: fieldValue,
        activeFieldInfo: r2State.highlightsFieldInfo,
        originalDispatches: [...r2State.editDispatches],
        originalHighlightDispatches: [...highlightDispatches],
        images: zionSlsState.images,
        sharedElements: currentFieldsOnHighlights,
        selectedHighlights: attachedHighlights,
        fieldInEdit: activeField,
        selectedPersonId: r2State.selectedPersonId,
        involvedPersons: personsFromField,
        types: zionSlsState.types,
      })
      handleSave(dispatchData)
    },
    [
      activeField,
      attachedHighlights,
      currentFieldsOnHighlights,
      fieldValue,
      getHighlightDispatches,
      handleSave,
      personsFromField,
      r2State.editDispatches,
      r2State.highlightsFieldInfo,
      r2State.selectedPersonId,
      zionSlsState.images,
      zionSlsState.types,
    ]
  )

  useUnsavedChangesOverlay({
    methods: formMethods,
    overlay: unsavedChangesOverlay,
    forceWarning: true,
    panelId: 'editSharedHighlightPage2',
  })

  /**
   * Main form submission handler
   * Routes to appropriate save function based on person and field state
   *
   * Flow logic:
   * 1. New person → Prepare dispatches and store highlights/field info for when person is created
   * 2. Existing person + New field → Prepare dispatches for new field and link to selected persons
   * 3. Existing person + Existing field → Prepare dispatches to update field associations across persons
   */
  const handleSubmit = (values) => {
    if (isNewPerson) {
      prepareDispatchesForNewPerson(values)
    } else if (isNewField) {
      prepareDispatchesForNewField(values)
    } else {
      prepareDispatchesForExistingFields(values)
    }
  }

  return (
    <InfoSheetPanel
      title={t('shared-field-via-highlight.title', 'Add Linked Field')}
      headingColor="yellow"
      {...{ ...editSharedHighlightPage2Overlay, close: handleClose }}
      footer={
        <Row alignX="end" gutters="md">
          <Button
            data-testid="panel-back-button"
            emphasis="low"
            type="submit"
            form=""
            onClick={handleClose}
            disabled={saving}
          >
            {t('edit-shared-field-footer.back', 'Back')}
          </Button>

          <Button
            data-testid="panel-forward-button"
            emphasis="high"
            disabled={saving}
            Icon={saving && NoticeLoading}
            form="add-highlight-linked-fields"
            type="submit"
            ref={saveButtonRef}
          >
            {isNewPerson ? t('edit-shared-field-footer.next', 'Next') : t('edit-shared-field-footer.save', 'Save')}
          </Button>
        </Row>
      }
    >
      <ZionForm onSubmit={handleSubmit} methods={formMethods} id="add-highlight-linked-fields">
        <PanelHeader
          heading={t('add-highlight-linked-fields.heading', '{fieldType}: {fieldValue}', {
            fieldType: fieldTypeLabel,
            fieldValue: linkedFieldText,
          })}
          caption={t('add-highlight-linked-fields.instructions.heading', 'Fix any mistakes that you see.')}
          headingSize="H5"
        />
        <Separator />
        <H6>{headerText}</H6>
        {personsSharingFieldOnImage.map((person) => (
          <SharedHighlightPersonCheckbox
            key={person.id}
            person={person}
            isNewPerson={isNewPerson}
            fieldValue={fieldValue}
            disabled={moreThanThirtyPersonsOnHighlights}
          />
        ))}
        {allTheArksExceptCurrent &&
          Object.entries(allTheArksExceptCurrent).map(([ark, persons]) => {
            // Shouldn't this be object notation?
            const arkIndex = thumbnails?.findIndex((thumbnail) => thumbnail.ark === ark)
            const pageNumber = arkIndex + 1
            return (
              <>
                <Separator size="xs" />
                <Divider
                  label={t('add-highlight-linked-fields.names-on-page-n.label', 'Names on Page {pageNumber}', {
                    pageNumber,
                  })}
                />
                <Button
                  Icon={ControlSplit}
                  onClick={() =>
                    r2Dispatch({
                      type: 'OPEN_SPLIT_SCREEN_TO_PAGE',
                      value: pageNumber,
                    })
                  }
                  emphasis="medium"
                >
                  {t('add-highlight-linked-fields.view-image-in-context.close.button', 'View in Context')}
                </Button>
                {persons.map((person) => (
                  <SharedHighlightPersonCheckbox
                    key={person.id}
                    person={person}
                    isNewPerson={isNewPerson}
                    fieldValue={fieldValue}
                    disabled={moreThanThirtyPersonsOnHighlights}
                  />
                ))}
              </>
            )
          })}
      </ZionForm>
      <UnsavedChangesOverlay
        overlay={unsavedChangesOverlay}
        onDiscard={onDiscard}
        onSave={() => saveButtonRef.current.click()}
        formId="review-highlight-linked-fields"
      />
    </InfoSheetPanel>
  )
}

export default SelectPersons
